import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import SEO from "../components/seo/SEO";
import { motion, AnimatePresence } from "framer-motion";
import {
    ChevronLeft, ChevronRight, MapPin, User, Calendar,
    Images, Play, ArrowRight,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import Footer from "../components/landing/Footer";
import Navigation from "../components/landing/Navigation";
import { api, BASE } from "../services/api";

const TABS = [
    { val: "all",        label: "Všetko"      },
    { val: "koncerty",   label: "Koncerty"    },
    { val: "firemne",    label: "Firemné"     },
    { val: "osvetlenie", label: "Osvetlenie"  },
    { val: "led",        label: "LED"         },
    { val: "ozvucenie",  label: "Ozvučenie"   },
    { val: "svadby",     label: "Svadby"      },
    { val: "instalacia", label: "Inštalácie"  },
];

const CAT_COLOR = {
    koncerty:   "#0099ff",
    firemne:    "#a855f7",
    osvetlenie: "#00BFFF",
    led:        "#f59e0b",
    ozvucenie:  "#22c55e",
    svadby:     "#ec4899",
    instalacia: "#f97316",
};

function resolveImg(url) {
    if (!url) return "";
    return url.startsWith("http") ? url : `${BASE}${url}`;
}

function bestImg(item) {
    return resolveImg(item.image_url || (item.images && item.images[0]) || "");
}

function fmtDate(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("sk-SK", { day: "numeric", month: "long", year: "numeric" }); }
    catch { return iso; }
}

// ── Hero Slider ───────────────────────────────────────────────────────────────
function HeroSlider({ items }) {
    const navigate = useNavigate();
    const [idx, setIdx]       = useState(0);
    const [paused, setPaused] = useState(false);
    const timerRef = useRef(null);

    const go = useCallback((dir) => {
        setIdx(i => (i + dir + items.length) % items.length);
        resetTimer();
    }, [items.length]);

    const resetTimer = () => {
        clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            if (!paused) setIdx(i => (i + 1) % items.length);
        }, 6000);
    };

    useEffect(() => {
        if (items.length < 2) return;
        timerRef.current = setInterval(() => setIdx(i => (i + 1) % items.length), 6000);
        return () => clearInterval(timerRef.current);
    }, [items.length]);

    if (!items.length) return null;

    const cur = items[idx];
    const allImgs = [cur.image_url, ...(cur.images || [])].filter(Boolean);
    const accent = CAT_COLOR[cur.category] || "#0099ff";

    return (
        <div className="relative w-full overflow-hidden"
            style={{ height: "min(75vh, 700px)" }}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}>

            {/* Slides */}
            <AnimatePresence mode="sync">
                <motion.div key={cur.id}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    transition={{ duration: 0.9, ease: "easeInOut" }}
                    className="absolute inset-0">
                    {/* Background photo */}
                    <img src={bestImg(cur)} alt={cur.title}
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ filter: "brightness(0.45)" }} />
                    {/* Gradient overlays */}
                    <div className="absolute inset-0 bg-gradient-to-r from-nova-black/80 via-nova-black/30 to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-t from-nova-black via-transparent to-nova-black/40" />
                </motion.div>
            </AnimatePresence>

            {/* Content */}
            <div className="relative h-full flex items-end pb-16 px-8 sm:px-16 z-10">
                <AnimatePresence mode="wait">
                    <motion.div key={`content-${cur.id}`}
                        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.5, delay: 0.15 }}
                        className="max-w-2xl">

                        {/* Category + counter */}
                        <div className="flex items-center gap-3 mb-4">
                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] px-2.5 py-1 rounded border"
                                style={{ color: accent, borderColor: `${accent}40`, background: `${accent}12` }}>
                                {cur.category}
                            </span>
                            <span className="font-mono text-[10px] text-zinc-500">{idx + 1} / {items.length}</span>
                        </div>

                        {/* Title */}
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.05] text-white mb-3">
                            {cur.title}
                        </h2>

                        {/* Meta */}
                        <div className="flex flex-wrap gap-4 mb-6">
                            {cur.client && (
                                <span className="flex items-center gap-1.5 text-zinc-400 text-sm">
                                    <User className="w-3.5 h-3.5" style={{ color: accent }} />
                                    {cur.client}
                                </span>
                            )}
                            {cur.location && (
                                <span className="flex items-center gap-1.5 text-zinc-400 text-sm">
                                    <MapPin className="w-3.5 h-3.5" style={{ color: accent }} />
                                    {cur.location}
                                </span>
                            )}
                            {cur.event_date && (
                                <span className="flex items-center gap-1.5 text-zinc-400 text-sm">
                                    <Calendar className="w-3.5 h-3.5" style={{ color: accent }} />
                                    {fmtDate(cur.event_date)}
                                </span>
                            )}
                        </div>

                        {/* CTAs */}
                        <div className="flex items-center gap-3">
                            <button onClick={() => navigate(`/referencie/${cur.id}`)}
                                className="flex items-center gap-2 h-11 px-6 rounded-xl font-semibold text-sm text-black transition-all hover:saturate-125"
                                style={{ background: accent }}>
                                <ArrowRight className="w-4 h-4" />
                                Zobraziť detail
                            </button>
                            {cur.video_url && (
                                <a href={cur.video_url} target="_blank" rel="noreferrer"
                                    className="flex items-center gap-2 h-11 px-5 rounded-xl border border-white/20 text-white text-sm font-medium hover:bg-white/8 transition-colors">
                                    <Play className="w-3.5 h-3.5" /> Video
                                </a>
                            )}
                        </div>

                        {/* Thumbnail strip (if gallery) */}
                        {allImgs.length > 1 && (
                            <div className="flex gap-2 mt-5 overflow-x-auto pb-1">
                                {allImgs.slice(0, 6).map((img, i) => (
                                    <button key={i} onClick={() => navigate(`/referencie/${cur.id}`)}
                                        className="w-14 h-10 rounded-md overflow-hidden shrink-0 border-2 border-white/10 hover:border-white/40 transition-all">
                                        <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                                {allImgs.length > 6 && (
                                    <div className="w-14 h-10 rounded-md bg-black/50 border border-white/10 flex items-center justify-center shrink-0">
                                        <span className="text-zinc-400 text-[10px] font-mono">+{allImgs.length - 6}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Navigation arrows */}
            {items.length > 1 && (
                <>
                    <button onClick={() => go(-1)}
                        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white hover:bg-black/70 hover:border-white/30 transition-all">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button onClick={() => go(1)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white hover:bg-black/70 hover:border-white/30 transition-all">
                        <ChevronRight className="w-5 h-5" />
                    </button>
                </>
            )}

            {/* Progress dots */}
            <div className="absolute bottom-5 right-8 z-20 flex gap-1.5">
                {items.map((_, i) => (
                    <button key={i} onClick={() => { setIdx(i); resetTimer(); }}
                        className="relative h-1 rounded-full overflow-hidden transition-all"
                        style={{ width: i === idx ? 28 : 12, background: i === idx ? accent : "rgba(255,255,255,0.2)" }}>
                        {i === idx && !paused && (
                            <motion.div className="absolute inset-y-0 left-0 rounded-full"
                                style={{ background: "rgba(255,255,255,0.4)" }}
                                initial={{ width: "0%" }} animate={{ width: "100%" }}
                                transition={{ duration: 6, ease: "linear" }}
                                key={`prog-${idx}`} />
                        )}
                    </button>
                ))}
            </div>

            {/* Bottom fade into page */}
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-nova-black to-transparent pointer-events-none z-10" />
        </div>
    );
}

// ── Lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ item, allItems, onClose }) {
    const [imgIdx,  setImgIdx]  = useState(0);
    const [itemIdx, setItemIdx] = useState(allItems.findIndex(i => i.id === item.id));
    const [cur, setCur] = useState(item);

    const switchItem = useCallback((dir) => {
        const next = itemIdx + dir;
        if (next < 0 || next >= allItems.length) return;
        setItemIdx(next);
        setCur(allItems[next]);
        setImgIdx(0);
    }, [itemIdx, allItems]);

    const curImgs = [cur.image_url, ...(cur.images || [])].filter(Boolean);
    const accent  = CAT_COLOR[cur.category] || "#0099ff";

    useEffect(() => {
        const handler = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft")  setImgIdx(i => Math.max(0, i - 1));
            if (e.key === "ArrowRight") setImgIdx(i => Math.min(curImgs.length - 1, i + 1));
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [curImgs.length, onClose]);

    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = ""; };
    }, []);

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/96 backdrop-blur-md flex flex-col">

            {/* Top bar */}
            <div className="flex items-center justify-between px-4 sm:px-8 py-4 shrink-0 border-b border-white/6">
                <div className="flex items-center gap-3 min-w-0">
                    <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border shrink-0"
                        style={{ color: accent, borderColor: `${accent}35`, background: `${accent}10` }}>
                        {cur.category}
                    </span>
                    <span className="font-heading font-bold text-white text-sm truncate">{cur.title}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <div className="hidden sm:flex items-center gap-1">
                        <button onClick={() => switchItem(-1)} disabled={itemIdx === 0}
                            className="p-1.5 rounded-lg text-zinc-500 hover:text-white disabled:opacity-20 transition-colors border border-white/8 hover:border-white/20">
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="font-mono text-[10px] text-zinc-600 px-1">{itemIdx + 1}/{allItems.length}</span>
                        <button onClick={() => switchItem(1)} disabled={itemIdx === allItems.length - 1}
                            className="p-1.5 rounded-lg text-zinc-500 hover:text-white disabled:opacity-20 transition-colors border border-white/8 hover:border-white/20">
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-zinc-400 hover:text-white transition-colors ml-1">
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Main */}
            <div className="flex-1 flex flex-col lg:flex-row min-h-0">
                {/* Image viewer */}
                <div className="flex-1 relative flex items-center justify-center bg-black/30 min-h-0 p-3 sm:p-6">
                    <AnimatePresence mode="wait">
                        <motion.img key={`${cur.id}-${imgIdx}`} src={resolveImg(curImgs[imgIdx])} alt={cur.title}
                            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                            transition={{ duration: 0.22 }}
                            className="max-w-full max-h-full object-contain rounded-xl select-none"
                            style={{ maxHeight: "calc(100vh - 200px)" }} draggable={false} />
                    </AnimatePresence>
                    {curImgs.length > 1 && (
                        <>
                            <button onClick={() => setImgIdx(i => Math.max(0, i - 1))} disabled={imgIdx === 0}
                                className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 text-white disabled:opacity-20 hover:bg-black/80 transition-all">
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <button onClick={() => setImgIdx(i => Math.min(curImgs.length - 1, i + 1))} disabled={imgIdx === curImgs.length - 1}
                                className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 text-white disabled:opacity-20 hover:bg-black/80 transition-all">
                                <ChevronRight className="w-5 h-5" />
                            </button>
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 font-mono text-[10px] text-zinc-400 bg-black/60 px-3 py-1 rounded-full backdrop-blur-sm">
                                {imgIdx + 1} / {curImgs.length}
                            </div>
                        </>
                    )}
                </div>

                {/* Sidebar */}
                <div className="lg:w-72 xl:w-80 border-t lg:border-t-0 lg:border-l border-white/6 overflow-y-auto shrink-0 flex flex-col">
                    {/* Thumbnail strip */}
                    {curImgs.length > 1 && (
                        <div className="flex gap-1.5 p-3 overflow-x-auto border-b border-white/6 lg:flex-wrap lg:overflow-x-visible shrink-0">
                            {curImgs.map((img, i) => (
                                <button key={i} onClick={() => setImgIdx(i)}
                                    className={`shrink-0 w-14 h-10 rounded-md overflow-hidden border-2 transition-all ${i === imgIdx ? "border-nova-cyan scale-105" : "border-transparent hover:border-white/30"}`}>
                                    <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Video embed */}
                    {cur.video_url && (() => {
                        const ytMatch = cur.video_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
                        const vmMatch = cur.video_url.match(/vimeo\.com\/(\d+)/);
                        const embedUrl = ytMatch
                            ? `https://www.youtube.com/embed/${ytMatch[1]}`
                            : vmMatch ? `https://player.vimeo.com/video/${vmMatch[1]}` : null;
                        return embedUrl ? (
                            <div className="shrink-0 border-b border-white/6">
                                <div className="aspect-video">
                                    <iframe src={embedUrl} title="Video" className="w-full h-full"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen />
                                </div>
                            </div>
                        ) : null;
                    })()}

                    {/* Meta */}
                    <div className="p-5 space-y-5 flex-1">
                        <div>
                            <div className="font-heading font-black text-white text-lg leading-tight mb-2">{cur.title}</div>
                            {cur.description && (
                                <div className="text-zinc-400 text-sm leading-relaxed prose-sm"
                                    dangerouslySetInnerHTML={{ __html: cur.description }} />
                            )}
                        </div>

                        <div className="space-y-3">
                            {cur.client && (
                                <div className="flex items-start gap-2.5">
                                    <User className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: accent }} />
                                    <div>
                                        <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-600 mb-0.5">Klient</div>
                                        <div className="text-white text-sm font-medium">{cur.client}</div>
                                    </div>
                                </div>
                            )}
                            {cur.location && (
                                <div className="flex items-start gap-2.5">
                                    <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: accent }} />
                                    <div>
                                        <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-600 mb-0.5">Miesto</div>
                                        <div className="text-white text-sm">{cur.location}</div>
                                    </div>
                                </div>
                            )}
                            {cur.event_date && (
                                <div className="flex items-start gap-2.5">
                                    <Calendar className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: accent }} />
                                    <div>
                                        <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-600 mb-0.5">Dátum</div>
                                        <div className="text-white text-sm">{fmtDate(cur.event_date)}</div>
                                    </div>
                                </div>
                            )}
                            {cur.images?.length > 0 && (
                                <div className="flex items-start gap-2.5">
                                    <Images className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: accent }} />
                                    <div>
                                        <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-600 mb-0.5">Galéria</div>
                                        <div className="text-white text-sm">{curImgs.length} fotiek</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {cur.tags?.length > 0 && (
                            <div>
                                <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider text-zinc-600 mb-2">
                                    <Tag className="w-3 h-3" /> Tagy
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {cur.tags.map(t => (
                                        <span key={t} className="px-2 py-0.5 rounded-full bg-nova-surface border border-white/10 text-xs text-zinc-300">{t}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

// ── Reference card ────────────────────────────────────────────────────────────
function RefCard({ item, index, size }) {
    const navigate = useNavigate();
    const allImgs = [item.image_url, ...(item.images || [])].filter(Boolean);
    const accent  = CAT_COLOR[item.category] || "#0099ff";

    // size: "hero" (2×2), "wide" (2×1), "normal" (1×1)
    const cls = size === "hero"  ? "md:col-span-2 md:row-span-2" :
                size === "wide"  ? "md:col-span-2" : "";

    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.3, delay: index * 0.04 }}
            onClick={() => navigate(`/referencie/${item.id}`)}
            className={`relative overflow-hidden rounded-2xl group cursor-pointer border border-white/6 hover:border-white/20 transition-all duration-300 ${cls}`}
        >
            {/* Photo */}
            <img src={bestImg(item)} alt={item.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy" />

            {/* Base gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
            {/* Hover darkening */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />

            {/* Top badges */}
            <div className="absolute top-3 left-3 flex items-center gap-2">
                <span className="font-mono text-[9px] uppercase tracking-wider px-2 py-1 rounded-full border backdrop-blur-sm"
                    style={{ color: accent, borderColor: `${accent}35`, background: `rgba(0,0,0,0.5)` }}>
                    {item.category}
                </span>
            </div>
            {allImgs.length > 1 && (
                <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm border border-white/10">
                    <Images className="w-3 h-3 text-nova-cyan" />
                    <span className="font-mono text-[9px] text-zinc-300">{allImgs.length}</span>
                </div>
            )}
            {item.video_url && (
                <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm border border-white/10"
                    style={{ right: allImgs.length > 1 ? "3.5rem" : "0.75rem" }}>
                    <Play className="w-3 h-3 text-nova-cyan fill-nova-cyan" />
                </div>
            )}

            {/* Thumbnail hover strip */}
            {allImgs.length > 2 && (
                <div className="absolute top-0 left-0 right-0 flex gap-1 p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                    {allImgs.slice(1, 5).map((img, i) => (
                        <div key={i} className="w-12 h-9 rounded overflow-hidden border border-white/20 shrink-0">
                            <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                        </div>
                    ))}
                    {allImgs.length > 5 && (
                        <div className="w-12 h-9 rounded bg-black/70 border border-white/20 flex items-center justify-center shrink-0">
                            <span className="text-[9px] text-zinc-400 font-mono">+{allImgs.length - 5}</span>
                        </div>
                    )}
                </div>
            )}

            {/* Bottom info */}
            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                <div className="transform translate-y-1 group-hover:translate-y-0 transition-transform duration-300">
                    <div className="font-heading font-black text-white leading-tight mb-1"
                        style={{ fontSize: size === "hero" ? "1.25rem" : "0.9rem" }}>
                        {item.title}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        {item.client && (
                            <span className="text-zinc-400 text-[10px] font-mono flex items-center gap-1">
                                <User className="w-2.5 h-2.5" />{item.client}
                            </span>
                        )}
                        {item.location && (
                            <span className="text-zinc-400 text-[10px] font-mono flex items-center gap-1">
                                <MapPin className="w-2.5 h-2.5" />{item.location}
                            </span>
                        )}
                        {item.event_date && (
                            <span className="text-zinc-400 text-[10px] font-mono flex items-center gap-1">
                                <Calendar className="w-2.5 h-2.5" />{fmtDate(item.event_date)}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Hover ring */}
            <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-transparent group-hover:ring-white/20 transition-all duration-300" />
        </motion.div>
    );
}

// ── Grid layout helper ────────────────────────────────────────────────────────
// Creates an editorial pattern: hero, normal, normal, wide, normal, normal, normal, normal...
function getCardSize(index) {
    const pos = index % 6;
    if (pos === 0) return "hero";
    if (pos === 5) return "wide";
    return "normal";
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ReferencePage() {
    const [filter,   setFilter]   = useState("all");
    const [gallery,  setGallery]  = useState([]);
    const [reviews,  setReviews]  = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [reviewIdx,setReviewIdx]= useState(0);

    useEffect(() => {
        Promise.all([api.getReferencie(), api.getTestimonials()])
            .then(([refs, tests]) => { setGallery(refs); setReviews(tests); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const filtered = filter === "all" ? gallery : gallery.filter(i => i.category === filter);

    // Top items for hero slider: prefer ones with images, take first 5
    const sliderItems = gallery.filter(i => i.image_url || i.images?.length).slice(0, 6);

    return (
        <div className="min-h-screen bg-nova-black text-white">
            <SEO
                title="Referencie & Realizácie"
                description="Prezrite si naše realizácie — live koncerty, firemné eventy, svadby a festivaly po celom Slovensku. Fotogaléria s viac ako 300 projektmi."
                keywords="referencie, realizácie, ozvučenie eventov, osvetlenie pódií, live produkcia, koncerty Slovensko, firemné akcie, svadby, festivaly"
                canonical="/referencie"
            />

            <Navigation />

            {/* Hero Slider */}
            {!loading && sliderItems.length > 0 && (
                <HeroSlider items={sliderItems} />
            )}
            {loading && (
                <div className="w-full bg-nova-surface/20 animate-pulse" style={{ height: "min(75vh, 700px)" }} />
            )}

            {/* Heading + filters */}
            <div className="max-w-7xl mx-auto px-6 sm:px-12 pt-16 pb-8">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                    className="mb-10">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-nova-cyan mb-3">/ Všetky realizácie</div>
                    <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight">
                        Projekty, ktoré sme <span className="text-gradient">technicky pokryli</span>
                    </h1>
                </motion.div>

                <Tabs value={filter} onValueChange={v => setFilter(v)}>
                    <TabsList className="flex flex-wrap h-auto bg-transparent gap-2 p-0 justify-start mb-10">
                        {TABS.map(t => (
                            <TabsTrigger key={t.val} value={t.val}
                                className="rounded-sm px-5 py-2 text-sm font-mono uppercase tracking-wide border border-white/8 bg-transparent text-zinc-500 data-[state=active]:border-nova-cyan/60 data-[state=active]:text-nova-cyan data-[state=active]:bg-nova-cyan/5 transition-all">
                                {t.label}
                                {t.val !== "all" && !loading && (
                                    <span className="ml-1.5 text-[10px] opacity-50">
                                        {gallery.filter(i => i.category === t.val).length || ""}
                                    </span>
                                )}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>

                {/* Editorial grid */}
                {loading ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 auto-rows-[200px] md:auto-rows-[220px] mb-24">
                        {[...Array(8)].map((_, i) => (
                            <div key={i} className={`rounded-2xl bg-white/4 animate-pulse ${i % 6 === 0 ? "md:col-span-2 md:row-span-2" : i % 6 === 5 ? "md:col-span-2" : ""}`} />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="py-24 text-center text-zinc-600 text-sm mb-24">Žiadne referencie v tejto kategórii.</div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 auto-rows-[200px] md:auto-rows-[220px] mb-24">
                        <AnimatePresence mode="popLayout">
                            {filtered.map((item, i) => (
                                <RefCard
                                    key={`${filter}-${item.id}`}
                                    item={item}
                                    index={i}
                                    size={getCardSize(i)}
                                />
                            ))}
                        </AnimatePresence>
                    </div>
                )}

                {/* Testimonials */}
                {reviews.length > 0 && (
                    <div className="mb-20">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-nova-cyan mb-4">/ Čo hovoria klienti</div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-10">
                            Slová, ktoré nás <span className="text-nova-cyan">zaväzujú</span>
                        </h2>
                        <AnimatePresence mode="wait">
                            <motion.div key={reviewIdx}
                                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.4 }}
                                className="border border-white/8 bg-nova-surface/40 rounded-xl p-8 sm:p-12 max-w-3xl">
                                <div className="text-[4rem] leading-none font-heading font-black text-nova-cyan/10 select-none mb-1">"</div>
                                <p className="font-heading text-xl sm:text-2xl font-medium text-white leading-[1.4] tracking-tight">
                                    {reviews[reviewIdx].quote}
                                </p>
                                <div className="mt-8 pt-6 border-t border-white/8 flex items-center justify-between">
                                    <div>
                                        <div className="font-heading font-bold text-white text-sm">{reviews[reviewIdx].author}</div>
                                        <div className="text-zinc-500 text-xs mt-0.5">{reviews[reviewIdx].role}</div>
                                    </div>
                                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-nova-cyan/50 border border-nova-cyan/15 px-2.5 py-1 rounded-sm">
                                        {reviews[reviewIdx].event}
                                    </div>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                        <div className="flex gap-2 mt-6">
                            {reviews.map((_, i) => (
                                <button key={i} onClick={() => setReviewIdx(i)}
                                    className={`h-1.5 rounded-full transition-all ${i === reviewIdx ? "w-8 bg-nova-cyan" : "w-4 bg-white/15 hover:bg-white/30"}`} />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
}
